import { useApolloClient, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  ApplicantCasesActiveCaseListContainerCredentialVerificationCasesV1ByApplicantQueryDocument,
  ApplicantCasesActiveCaseListContainerSendReportCasesV1ByApplicantQueryDocument,
  CasesCredentialVerificationV1CreateContainerCredentialVerificationCaseV1DeleteDocument,
  CasesCredentialVerificationV1CreateContainerCredentialVerificationCaseV1Document,
  CasesCredentialVerificationV1CreateContainerCredentialVerificationCaseV1LazyQueryDocument,
  CasesCredentialVerificationV1CreateContainerCredentialVerificationCaseV1UpdateDocument,
  CasesCredentialVerificationV1CreateContainerEntitiesDocument,
  CasesCredentialVerificationV1CreateContainerSendReportCasesV1ByApplicantDocument,
  CredentialVerificationCaseV1Application,
  CredentialVerificationCaseV1ApplicationInput,
  Entity,
} from "../../../../../../../../generated";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { Create } from "./create";
import { lookupCountryQuery } from "./case-details-country-query";
import { Country } from "../../../../../../../../types/country";
import { ComponentQueryLoader } from "../../../../../../../shared/component-query-loader";
import { useEffect, useState } from "react";
import { AzureBlobResponse } from "../../../../../../../shared/file-upload/azure-upload";

interface CreateContainerProps {
  caseId: string;
  applicantUserId: string;
}

export const CreateContainer: React.FC<CreateContainerProps> = (props) => {
  const navigate = useNavigate();
  const client = useApolloClient();
  const [formLoading, setFormLoading] = useState<boolean>(false);

  const { data, loading, error } = useQuery(CasesCredentialVerificationV1CreateContainerCredentialVerificationCaseV1Document, {
    variables: {
      credentialVerificationCaseV1Id: props.caseId
    }
  });

  // use this
  const [_paymentPollingData, { data: pollingData, startPolling, stopPolling }] = useLazyQuery(
    CasesCredentialVerificationV1CreateContainerCredentialVerificationCaseV1LazyQueryDocument,
    {
      variables: { credentialVerificationCaseV1Id: props.caseId }
    }
  );

  const [_sendReportCasesV1ByApplicant, { data: sendCasesByApplicantData, startPolling: startPollingSendCases, stopPolling: stopPollingSendCases }] = useLazyQuery(
    CasesCredentialVerificationV1CreateContainerSendReportCasesV1ByApplicantDocument,
    {
      variables: {
        applicantId: props.applicantUserId
      }
    }
  );

  useEffect(() => {
    if (sendCasesByApplicantData?.sendReportCasesV1ByApplicant && sendCasesByApplicantData?.sendReportCasesV1ByApplicant.length > 0) {
      const sendReportCase = sendCasesByApplicantData.sendReportCasesV1ByApplicant.find((sendCase) => {
        return (
          sendCase?.caseDetails?.application?.credential?.id === pollingData?.credentialVerificationCaseV1?.id &&
          sendCase?.caseDetails?.application?.destination?.id === pollingData?.credentialVerificationCaseV1?.caseDetails?.application?.sendDestination?.id
        );
      });

      console.log('sendReportCase found: ', sendReportCase);
      if (sendReportCase) {
        stopPollingSendCases();
        client.cache.updateQuery(
          {
            query: ApplicantCasesActiveCaseListContainerSendReportCasesV1ByApplicantQueryDocument,
            variables: { applicantId: props.applicantUserId }
          },
          (existingData) => {
            if (!existingData) {
              return { sendReportCasesV1ByApplicant: [sendReportCase] };
            }

            return {
              ...existingData,
              sendReportCasesV1ByApplicant: [sendReportCase, ...(existingData.sendReportCasesV1ByApplicant ?? [])]
            };
          }
        );
        setFormLoading(false);
        navigate(`../active/v1`);
      }
    }
  }, [sendCasesByApplicantData]);

  // use this
  useEffect(() => {
    if (pollingData?.credentialVerificationCaseV1?.financeDetails?.transactions?.submission?.transactionReference?.isSuccess === true) {
      stopPolling();
      startPollingSendCases(1000);
    } else if (pollingData?.credentialVerificationCaseV1?.financeDetails?.transactions?.submission?.transactionReference?.isSuccess === false) {
      stopPolling();
      setFormLoading(false);
      message.error('Payment failed: ' + pollingData?.credentialVerificationCaseV1?.financeDetails?.transactions?.submission?.transactionReference?.errorMessage);
    }
  }, [pollingData]);

  const { data: entityData, loading: entityLoading, error: entityError } = useQuery(CasesCredentialVerificationV1CreateContainerEntitiesDocument);

  const [updateCredentialVerificationCase] = useMutation(CasesCredentialVerificationV1CreateContainerCredentialVerificationCaseV1UpdateDocument);

  const updateCredCaseCache = async () => {
    client.cache.updateQuery(
      {
        query: ApplicantCasesActiveCaseListContainerCredentialVerificationCasesV1ByApplicantQueryDocument,
        variables: { applicantId: props.applicantUserId }
      },
      (existingData) => ({
        credentialVerificationCasesV1ByApplicant: existingData?.credentialVerificationCasesV1ByApplicant?.filter(
          (item) => item?.id !== props.caseId
        ) ?? []
      })
    );
  };

  const [deleteCase] = useMutation(CasesCredentialVerificationV1CreateContainerCredentialVerificationCaseV1DeleteDocument, {
    onCompleted: (response) => {
      updateCredCaseCache()
        .then(() => {
          if (response?.credentialVerificationCaseV1Delete?.status?.success === true) {
            navigate('/applicant/cases');
            message.success('Case withdrawn successfully');
          } else {
            message.error('Failed to withdraw case');
          }
        })
        .catch((error) => {
          message.error('Failed to update credential case cache');
          console.error('Error updating credential case cache', error);
        });
    }
  });

  const [processPayment] = useMutation(CasesCredentialVerificationV1CreateContainerCredentialVerificationCaseV1UpdateDocument, {
    refetchQueries: [
      {
        query: CasesCredentialVerificationV1CreateContainerCredentialVerificationCaseV1Document,
        variables: {
          credentialVerificationCaseV1Id: props.caseId
        }
      }
    ],
    awaitRefetchQueries: true,
    onCompleted: (response) => {
      const { status, credentialVerificationCaseV1 } = response.credentialVerificationCaseV1Update || {};
      if (status?.success === true && credentialVerificationCaseV1?.financeDetails?.transactions?.submission?.transactionReference?.status === 'PENDING') {
        try {
          console.log('Polling started');
          startPolling(5000);
        } catch (error: any) {
          message.error(error.message);
          setFormLoading(false);
        }
      } else {
        message.error('Application submission failed: ' + status?.errorMessage);
        setFormLoading(false);
      }
    },
    onError: (error) => {
      console.log('Error ', error);
      message.error(`Error occurred while submitting application: ${error.message}`);
      setFormLoading(false);
    }
  });

  const handleOnUploadSuccess = async (response: AzureBlobResponse, fieldName: string) => {
    const input = {
      id: props.caseId,
      caseDetails: {
        application: {
          credentialAssets: {
            [fieldName]: response.blobUrl,
            [`${fieldName}Version`]: response.versionId
          }
        }
      }
    };
    await updateCredentialVerificationCase({
      variables: {
        input
      }
    });
  };

  const { data: countryData, loading: countryLoading, error: countryError } = useQuery(lookupCountryQuery);

  const handleSubmit = async (values: CredentialVerificationCaseV1ApplicationInput) => {
    setFormLoading(true);
    await processPayment({
      variables: {
        input: {
          id: props.caseId,
          caseDetails: {
            application: {
              ...values
            }
          },
          payment: {
            paymentAmount: data?.credentialVerificationCaseV1?.financeDetails?.serviceFee as number
          }
        }
      }
    });
  };

  const handleWithdrawRequest = async () => {
    setFormLoading(true);
    await deleteCase({
      variables: {
        input: {
          caseId: props.caseId
        }
      }
    });
    setFormLoading(false);
  };

  return (
    <ComponentQueryLoader
      error={error || entityError || countryError}
      loading={loading || entityLoading || countryLoading}
      hasData={data && entityData && countryData}
      hasDataComponent={
        <Create
          handleOnUploadSuccess={handleOnUploadSuccess}
          onSubmit={handleSubmit}
          onWithdrawRequest={handleWithdrawRequest}
          caseId={props.caseId}
          applicantUserId={props.applicantUserId}
          caseData={data?.credentialVerificationCaseV1?.caseDetails?.application as CredentialVerificationCaseV1Application}
          countryData={countryData?.countries?.eventPayload?.countries as Country[]}
          serviceFee={data?.credentialVerificationCaseV1?.financeDetails?.serviceFee as number}
          entities={entityData?.entities as Entity[]}
          formLoading={formLoading}
        />
      }
    />
  );
};
